import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/useToast';
import {
    Activity,
    AlertCircle,
    ArrowDownRight,
    ArrowUpRight,
    BarChart3,
    CheckCircle2,
    DollarSign,
    Download,
    Eye,
    Filter,
    LineChart,
    Target,
    TrendingUp,
    Users,
    X
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

interface BillingMetrics {
  totalRevenue: number;
  totalCost: number;
  profit: number;
  margin: number;
  invoiceCount: number;
  paidInvoices: number;
  pendingAmount: number;
  overdueAmount: number;
}

interface ChartData {
  month: string;
  sales: number;
  purchase: number;
  profit: number;
}

interface TopCustomer {
  id: string;
  name: string;
  revenue: number;
  invoices: number;
  growth: number;
}

const DEFAULT_PERIOD = '30';
const DEFAULT_REPORT_TYPE = 'overview';

const formatDateInput = (date: Date) => date.toISOString().split('T')[0];
const buildPresetRange = (period: string) => {
  const days = Number.parseInt(period, 10);
  if (Number.isNaN(days)) return null;
  const end = new Date();
  const start = new Date(end);
  start.setDate(end.getDate() - days);
  return {
    startDate: formatDateInput(start),
    endDate: formatDateInput(end)
  };
};

const MOCK_METRICS: BillingMetrics = {
  totalRevenue: 2850000,
  totalCost: 1890000,
  profit: 960000,
  margin: 33.68,
  invoiceCount: 145,
  paidInvoices: 128,
  pendingAmount: 485000,
  overdueAmount: 125000
};

const MOCK_CHART_DATA: ChartData[] = [
  { month: 'Jan', sales: 480000, purchase: 320000, profit: 160000 },
  { month: 'Feb', sales: 520000, purchase: 340000, profit: 180000 },
  { month: 'Mar', sales: 580000, purchase: 380000, profit: 200000 },
  { month: 'Apr', sales: 620000, purchase: 400000, profit: 220000 },
  { month: 'May', sales: 680000, purchase: 450000, profit: 230000 },
  { month: 'Jun', sales: 720000, purchase: 480000, profit: 240000 }
];

const MOCK_TOP_CUSTOMERS: TopCustomer[] = [
  { id: '1', name: 'TechCorp India Pvt Ltd', revenue: 485000, invoices: 28, growth: 15.2 },
  { id: '2', name: 'Mumbai Fashion House', revenue: 320000, invoices: 19, growth: 8.7 },
  { id: '3', name: 'Express Solutions Ltd', revenue: 275000, invoices: 16, growth: -2.1 },
  { id: '4', name: 'Global Trade Co', revenue: 190000, invoices: 12, growth: 22.4 },
  { id: '5', name: 'Logistics Plus', revenue: 145000, invoices: 8, growth: 5.8 }
];

const PERIOD_OPTIONS = [
  { value: '7', label: 'Last 7 Days' },
  { value: '30', label: 'Last 30 Days' },
  { value: '90', label: 'Last 3 Months' },
  { value: '180', label: 'Last 6 Months' },
  { value: '365', label: 'Last Year' },
  { value: 'custom', label: 'Custom Range' }
];

const REPORT_TYPES = [
  { value: 'overview', label: 'Overview', icon: BarChart3 },
  { value: 'revenue', label: 'Revenue Analysis', icon: TrendingUp },
  { value: 'customers', label: 'Customer Reports', icon: Users },
  { value: 'collections', label: 'Collections', icon: DollarSign },
  { value: 'profitability', label: 'Profitability', icon: Target }
];

const BillingReports = () => {
  const { toast } = useToast();
  const [selectedPeriod, setSelectedPeriod] = useState(DEFAULT_PERIOD);
  const [reportType, setReportType] = useState(DEFAULT_REPORT_TYPE);
  const [dateRange, setDateRange] = useState(() => {
    return buildPresetRange(DEFAULT_PERIOD) || {
      startDate: formatDateInput(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)),
      endDate: formatDateInput(new Date())
    };
  });
  const [selectedCustomer, setSelectedCustomer] = useState('all');
  const [isGenerating, setIsGenerating] = useState(false);
  const isCustomPeriod = selectedPeriod === 'custom';

  useEffect(() => {
    if (selectedPeriod === 'custom') return;
    const preset = buildPresetRange(selectedPeriod);
    if (!preset) return;
    setDateRange(preset);
  }, [selectedPeriod]);

  // Enhanced metrics calculations
  const enhancedMetrics = useMemo(() => {
    const collectionRate = (MOCK_METRICS.paidInvoices / MOCK_METRICS.invoiceCount) * 100;
    const overdueRate = (MOCK_METRICS.overdueAmount / MOCK_METRICS.totalRevenue) * 100;
    const avgInvoiceValue = MOCK_METRICS.totalRevenue / MOCK_METRICS.invoiceCount;
    const profitMargin = ((MOCK_METRICS.totalRevenue - MOCK_METRICS.totalCost) / MOCK_METRICS.totalRevenue) * 100;

    return {
      ...MOCK_METRICS,
      collectionRate,
      overdueRate,
      avgInvoiceValue,
      profitMargin
    };
  }, []);

  const selectedPeriodLabel = useMemo(() => {
    return PERIOD_OPTIONS.find(option => option.value === selectedPeriod)?.label ?? selectedPeriod;
  }, [selectedPeriod]);

  const selectedCustomerLabel = useMemo(() => {
    if (selectedCustomer === 'all') return 'All Customers';
    return MOCK_TOP_CUSTOMERS.find(customer => customer.id === selectedCustomer)?.name ?? 'Selected Customer';
  }, [selectedCustomer]);

  const activeFilterChips = useMemo(() => {
    const chips: Array<{ id: string; label: string; value: string; onClear: () => void }> = [];
    if (reportType !== DEFAULT_REPORT_TYPE) {
      const label = REPORT_TYPES.find(type => type.value === reportType)?.label ?? reportType;
      chips.push({
        id: 'type',
        label: 'Report',
        value: label,
        onClear: () => setReportType(DEFAULT_REPORT_TYPE)
      });
    }
    if (selectedPeriod !== DEFAULT_PERIOD) {
      const value = isCustomPeriod
        ? `${dateRange.startDate || 'Any'} → ${dateRange.endDate || 'Any'}`
        : selectedPeriodLabel;
      chips.push({
        id: 'period',
        label: 'Period',
        value,
        onClear: () => setSelectedPeriod(DEFAULT_PERIOD)
      });
    }
    if (selectedCustomer !== 'all') {
      chips.push({
        id: 'customer',
        label: 'Customer',
        value: selectedCustomerLabel,
        onClear: () => setSelectedCustomer('all')
      });
    }
    return chips;
  }, [
    dateRange.endDate,
    dateRange.startDate,
    isCustomPeriod,
    reportType,
    selectedCustomer,
    selectedCustomerLabel,
    selectedPeriod,
    selectedPeriodLabel
  ]);

  const clearFilters = () => {
    setReportType(DEFAULT_REPORT_TYPE);
    setSelectedCustomer('all');
    setSelectedPeriod(DEFAULT_PERIOD);
  };

  const generateReport = async (format: 'pdf' | 'excel' | 'csv') => {
    setIsGenerating(true);
    try {
      // Simulate report generation
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast({
        title: 'Report Generated Successfully!',
        description: `${reportType.toUpperCase()} report downloaded as ${format.toUpperCase()}`,
      });
    } catch (error) {
      toast({
        title: 'Generation Failed',
        description: 'Please try again later',
        variant: 'destructive'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const getGrowthIcon = (growth: number) => {
    if (growth > 0) return <ArrowUpRight className="h-4 w-4 text-green-500" />;
    if (growth < 0) return <ArrowDownRight className="h-4 w-4 text-red-500" />;
    return <Activity className="h-4 w-4 text-gray-500" />;
  };

  const getGrowthColor = (growth: number) => {
    if (growth > 0) return 'text-green-600';
    if (growth < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  return (
    <div className="space-y-6">
      {/* Enhanced Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-2xl font-bold tracking-tight">Billing Reports & Analytics</h3>
          <p className="text-muted-foreground">
            Comprehensive insights into your billing performance and trends
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
          <Button variant="outline" onClick={() => generateReport('pdf')} disabled={isGenerating} className="w-full sm:w-auto">
            <Download className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
          <Button variant="outline" onClick={() => generateReport('excel')} disabled={isGenerating} className="w-full sm:w-auto">
            <Download className="h-4 w-4 mr-2" />
            Export Excel
          </Button>
          <Button variant="outline" onClick={() => generateReport('csv')} disabled={isGenerating} className="w-full sm:w-auto">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Enhanced Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Report Configuration
          </CardTitle>
          <CardDescription>Configure your report parameters and date ranges</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Report Type</Label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_TYPES.map(type => {
                    const Icon = type.icon;
                    return (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          {type.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Time Period</Label>
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {isCustomPeriod && (
              <>
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label>Customer Filter</Label>
              <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Customers</SelectItem>
                  {MOCK_TOP_CUSTOMERS.map(customer => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {activeFilterChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Active filters
          </span>
          {activeFilterChips.map((chip) => (
            <Badge key={chip.id} variant="secondary" className="gap-1 pr-1 text-xs font-medium">
              <span className="max-w-[220px] truncate" title={`${chip.label}: ${chip.value}`}>
                {chip.label}: {chip.value}
              </span>
              <button
                type="button"
                onClick={chip.onClear}
                className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/70"
                aria-label={`Clear ${chip.label}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-muted-foreground">
          Reporting for {isCustomPeriod
            ? `${dateRange.startDate || 'Any'} → ${dateRange.endDate || 'Any'}`
            : selectedPeriodLabel.toLowerCase()}
          {selectedCustomer !== 'all' && <span className="ml-2">• {selectedCustomerLabel}</span>}
        </div>
        <Button
          variant="outline"
          onClick={clearFilters}
          size="sm"
          className="w-full sm:w-auto"
          disabled={activeFilterChips.length === 0}
        >
          <X className="h-4 w-4 mr-2" />
          Clear Filters
        </Button>
      </div>

      {/* Enhanced Metrics Dashboard */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">₹{enhancedMetrics.totalRevenue.toLocaleString()}</div>
            <div className="flex items-center gap-2 mt-2">
              {getGrowthIcon(12.5)}
              <p className={`text-xs font-medium ${getGrowthColor(12.5)}`}>
                +12.5% from last period
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Profit Margin</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{enhancedMetrics.profitMargin.toFixed(1)}%</div>
            <div className="mt-2">
              <Progress value={enhancedMetrics.profitMargin} className="h-2" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Target: 35%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Collection Rate</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{enhancedMetrics.collectionRate.toFixed(1)}%</div>
            <div className="mt-2">
              <Progress value={enhancedMetrics.collectionRate} className="h-2" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {enhancedMetrics.paidInvoices} of {enhancedMetrics.invoiceCount} invoices
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Outstanding</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">₹{enhancedMetrics.pendingAmount.toLocaleString()}</div>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="destructive" className="text-xs">
                ₹{enhancedMetrics.overdueAmount.toLocaleString()} Overdue
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dynamic Report Content */}
      <Tabs value={reportType} onValueChange={setReportType} className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          {REPORT_TYPES.map(type => {
            const Icon = type.icon;
            return (
              <TabsTrigger key={type.value} value={type.value} className="flex items-center gap-2">
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{type.label}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Revenue Trend Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <LineChart className="h-5 w-5" />
                  Revenue Trend
                </CardTitle>
                <CardDescription>Monthly revenue and profit analysis</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {MOCK_CHART_DATA.map((data, index) => (
                    <div key={index} className="flex items-center justify-between py-2">
                      <div className="flex items-center gap-3">
                        <div className="w-12 text-sm font-medium">{data.month}</div>
                        <div className="flex-1">
                          <div className="h-2 bg-gray-200 rounded overflow-hidden">
                            <div 
                              className="h-full bg-blue-600 rounded" 
                              style={{ width: `${(data.sales / 800000) * 100}%` }}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">₹{data.sales.toLocaleString()}</div>
                        <div className="text-xs text-muted-foreground">
                          {((data.profit / data.sales) * 100).toFixed(1)}% margin
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Top Customers */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Top Customers
                </CardTitle>
                <CardDescription>Highest revenue generating customers</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {MOCK_TOP_CUSTOMERS.slice(0, 5).map((customer, index) => (
                    <div key={customer.id} className="flex items-center justify-between py-2">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm font-medium">
                          {index + 1}
                        </div>
                        <div>
                          <div className="font-medium">{customer.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {customer.invoices} invoices
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">₹{customer.revenue.toLocaleString()}</div>
                        <div className={`text-xs flex items-center gap-1 ${getGrowthColor(customer.growth)}`}>
                          {getGrowthIcon(customer.growth)}
                          {Math.abs(customer.growth).toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="revenue" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Revenue vs Cost Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {MOCK_CHART_DATA.map((data, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium">{data.month}</span>
                        <span>₹{data.sales.toLocaleString()}</span>
                      </div>
                      <div className="relative">
                        <div className="h-8 bg-gray-100 rounded overflow-hidden">
                          <div 
                            className="h-full bg-blue-600" 
                            style={{ width: `${(data.sales / 800000) * 100}%` }}
                          />
                          <div 
                            className="absolute top-0 h-full bg-red-400 opacity-70" 
                            style={{ width: `${(data.purchase / 800000) * 100}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-muted-foreground mt-1">
                          <span>Sales: ₹{data.sales.toLocaleString()}</span>
                          <span>Cost: ₹{data.purchase.toLocaleString()}</span>
                          <span className="font-medium text-green-600">
                            Profit: ₹{data.profit.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Revenue Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold">₹28.5L</div>
                    <div className="text-sm text-muted-foreground">Total Revenue</div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm">Express Delivery</span>
                      <span className="font-medium">65%</span>
                    </div>
                    <Progress value={65} className="h-2" />

                    <div className="flex justify-between">
                      <span className="text-sm">Standard Delivery</span>
                      <span className="font-medium">25%</span>
                    </div>
                    <Progress value={25} className="h-2" />

                    <div className="flex justify-between">
                      <span className="text-sm">Economy Delivery</span>
                      <span className="font-medium">10%</span>
                    </div>
                    <Progress value={10} className="h-2" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="customers" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Customer Performance Analysis
              </CardTitle>
              <CardDescription>Detailed customer metrics and growth analysis</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rank</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Revenue</TableHead>
                      <TableHead>Invoices</TableHead>
                      <TableHead>Avg. Invoice</TableHead>
                      <TableHead>Growth</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {MOCK_TOP_CUSTOMERS.map((customer, index) => (
                      <TableRow key={customer.id}>
                        <TableCell>
                          <Badge variant="outline">#{index + 1}</Badge>
                        </TableCell>
                        <TableCell className="font-medium">{customer.name}</TableCell>
                        <TableCell>₹{customer.revenue.toLocaleString()}</TableCell>
                        <TableCell>{customer.invoices}</TableCell>
                        <TableCell>₹{(customer.revenue / customer.invoices).toLocaleString()}</TableCell>
                        <TableCell>
                          <div className={`flex items-center gap-2 ${getGrowthColor(customer.growth)}`}>
                            {getGrowthIcon(customer.growth)}
                            {customer.growth > 0 ? '+' : ''}{customer.growth.toFixed(1)}%
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="outline">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="collections" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Collection Performance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">88.3%</div>
                      <div className="text-sm text-green-700">Collection Rate</div>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                      <div className="text-2xl font-bold text-orange-600">15.2</div>
                      <div className="text-sm text-orange-700">Avg. Collection Days</div>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>Paid on Time</span>
                      <span className="font-medium text-green-600">75%</span>
                    </div>
                    <Progress value={75} className="h-2" />

                    <div className="flex justify-between">
                      <span>Paid Late</span>
                      <span className="font-medium text-yellow-600">13%</span>
                    </div>
                    <Progress value={13} className="h-2" />

                    <div className="flex justify-between">
                      <span>Overdue</span>
                      <span className="font-medium text-red-600">12%</span>
                    </div>
                    <Progress value={12} className="h-2" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Aging Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-medium">0-30 Days</div>
                      <div className="text-sm text-muted-foreground">Current</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">₹3.2L</div>
                      <div className="text-sm text-green-600">68%</div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
                    <div>
                      <div className="font-medium">31-60 Days</div>
                      <div className="text-sm text-muted-foreground">Aging</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">₹1.2L</div>
                      <div className="text-sm text-yellow-600">25%</div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                    <div>
                      <div className="font-medium">60+ Days</div>
                      <div className="text-sm text-muted-foreground">Overdue</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">₹0.3L</div>
                      <div className="text-sm text-red-600">7%</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="profitability" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Profit Metrics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">₹9.6L</div>
                    <div className="text-sm text-muted-foreground">Total Profit</div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>Gross Margin</span>
                      <span className="font-medium">45.2%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Net Margin</span>
                      <span className="font-medium">33.7%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Operating Margin</span>
                      <span className="font-medium">38.1%</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Service-wise Profitability</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Express Delivery</span>
                      <span className="font-medium">38.5% margin • ₹6.2L profit</span>
                    </div>
                    <Progress value={38.5} className="h-3" />
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Standard Delivery</span>
                      <span className="font-medium">28.2% margin • ₹2.8L profit</span>
                    </div>
                    <Progress value={28.2} className="h-3" />
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Economy Delivery</span>
                      <span className="font-medium">22.1% margin • ₹0.6L profit</span>
                    </div>
                    <Progress value={22.1} className="h-3" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BillingReports;
